import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { accessApi, type Role } from '@/api/access-api';
import { usePermission } from '@/auth/use-permission';
import { ACCESS_MANAGE_ROLES } from '@loanaudit/shared';

import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Input } from '@/acc-ui/primitives/Input';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Sheet } from '@/acc-ui/primitives/Sheet';
import { PermissionPicker } from '@/acc-ui/components/PermissionPicker';
import { ErrorState } from '@/acc-ui/primitives/Layout';

export function AccessRolesTab() {
  const qc = useQueryClient();
  const { has } = usePermission();
  const canManage = has(ACCESS_MANAGE_ROLES);

  const [editing, setEditing] = useState<Role | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: accessApi.listRoles });
  const permsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: accessApi.listPermissions,
    enabled: canManage,
  });

  const columns: Column<Role>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Role',
        cell: (r) => (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-text-primary">{r.name}</span>
              {r.isBuiltin && <Badge tone="info">Built-in</Badge>}
            </div>
            <span className="text-caption text-text-tertiary">
              <code className="font-mono">{r.key}</code>
              {r.description && ` · ${r.description}`}
            </span>
          </div>
        ),
      },
      {
        key: 'permissions',
        header: 'Permissions',
        cell: (r) => (
          <span className="text-label text-text-secondary tabular-nums">
            {r.permissions.length}
          </span>
        ),
        align: 'right',
        width: '8rem',
      },
      {
        key: 'userCount',
        header: 'Users',
        cell: (r) => (
          <span className="text-label text-text-secondary tabular-nums">{r.userCount}</span>
        ),
        align: 'right',
        width: '6rem',
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        {canManage && (
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
            New role
          </Button>
        )}
      </div>

      {rolesQuery.isError ? (
        <ErrorState
          description="Could not load roles."
          action={<Button onClick={() => rolesQuery.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<Role>
          columns={columns}
          rows={rolesQuery.data ?? []}
          rowKey={(r) => r.key}
          page={1}
          pageSize={rolesQuery.data?.length ?? 25}
          total={rolesQuery.data?.length ?? 0}
          onPageChange={() => undefined}
          loading={rolesQuery.isLoading}
          onRowClick={canManage ? setEditing : undefined}
          emptyMessage="No roles yet — only the five built-ins ship by default."
        />
      )}

      {canManage && (
        <CreateRoleDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          permissions={permsQuery.data ?? []}
          onCreated={() => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ['roles'] });
          }}
        />
      )}

      {canManage && editing && (
        <EditRoleSheet
          role={editing}
          permissions={permsQuery.data ?? []}
          onOpenChange={(v) => !v && setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['roles'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------- Create dialog ----------------

function CreateRoleDialog({
  open,
  onOpenChange,
  permissions,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  permissions: { key: string; module: string; action: string; description: string }[];
  onCreated: () => void;
}) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setKey('');
    setName('');
    setDescription('');
    setSelected(new Set());
    setErr(null);
  };

  const mut = useMutation({
    mutationFn: () =>
      accessApi.createRole({
        key: key.trim(),
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(selected),
      }),
    onSuccess: () => {
      reset();
      onCreated();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Could not create role'),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
      title="New role"
      description="A role is a named set of permissions. Users get one or more roles. Keys are lowercase snake_case and cannot be changed after creation."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              if (!key.trim() || !name.trim()) {
                setErr('Key and name are required');
                return;
              }
              if (!/^[a-z][a-z0-9_]*$/.test(key.trim())) {
                setErr('Key must be lowercase snake_case (e.g. branch_auditor)');
                return;
              }
              mut.mutate();
            }}
            loading={mut.isPending}
          >
            Create role
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Key <span className="text-red-700">*</span></span>
          <Input
            placeholder="branch_auditor"
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase())}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Display name <span className="text-red-700">*</span></span>
          <Input
            placeholder="Branch Auditor"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">Description</span>
        <Textarea
          rows={2}
          placeholder="What is this role for? Who should get it?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <span className="text-caption font-semibold text-text-secondary">
          Permissions ({selected.size} selected)
        </span>
        <PermissionPicker
          permissions={permissions}
          selected={selected}
          onChange={setSelected}
        />
      </div>

      {err && (
        <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {err}
        </div>
      )}
    </Dialog>
  );
}

// ---------------- Edit sheet ----------------

function EditRoleSheet({
  role,
  permissions,
  onOpenChange,
  onSaved,
}: {
  role: Role;
  permissions: { key: string; module: string; action: string; description: string }[];
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      accessApi.updateRole(role.key, {
        name: name.trim(),
        description: description.trim(),
        // Built-in roles: cannot change permissions per backend; backend
        // enforces, but we avoid sending to get a cleaner UX.
        permissions: role.isBuiltin ? undefined : Array.from(selected),
      }),
    onSuccess: onSaved,
    onError: (e) => setErr(e instanceof Error ? e.message : 'Could not save role'),
  });

  return (
    <Sheet open onOpenChange={onOpenChange} side="right" title={role.name} widthPx={520}>
      <div className="flex flex-col gap-4">
        <div className="p-3 bg-white/60 rounded-glass-sm border border-glass-hairline">
          <div className="flex items-center justify-between gap-3">
            <code className="font-mono text-label text-text-primary">{role.key}</code>
            {role.isBuiltin && <Badge tone="info">Built-in</Badge>}
          </div>
          {role.isBuiltin && (
            <p className="mt-2 text-micro text-text-tertiary">
              Built-in roles have fixed permission sets. You can update the display name and
              description, but not the permissions.
            </p>
          )}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Display name <span className="text-red-700">*</span></span>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-caption font-semibold text-text-secondary">Description</span>
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-caption font-semibold text-text-secondary">
            Permissions ({selected.size}{role.isBuiltin ? ' — read-only' : ''})
          </span>
          <PermissionPicker
            permissions={permissions}
            selected={selected}
            onChange={setSelected}
            disabled={role.isBuiltin}
          />
        </div>

        {err && (
          <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending}>
            Save changes
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
